import type { PrioVersionNumber, SecretLoadStatus } from '@/components/landing-page/landing-page.types';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { getClientSecretUrl } from '@/components/landing-page/utils/functions';
import usePath from '@/hooks/usePath';
import { ConfigSlice } from '@/redux/config';
import { GeneralSlice } from '@/redux/general';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { getClientSecretTargetUrl, whichUrlShouldBePassed } from '@/utils/utils';

let MAX_TRYING_API_CALL = 10;

export default function useLoadClientSecret(version: PrioVersionNumber) {
  const path = usePath();
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<SecretLoadStatus>('idle');
  const [clientSecret, setClientSecret] = useState('');
  const dontLookForSecret = useAppSelector(ConfigSlice.selectors.dontLookForSecret);
  const leadId = useAppSelector(GeneralSlice.selectors.leadId);
  const loseLead = useAppSelector(GeneralSlice.selectors.loseLead);

  const getClientSecret = async (leadId: string): Promise<void> => {
    if (dontLookForSecret) {
      setStatus('success');
      return;
    }
    try {
      const url = getClientSecretUrl() + '?externalId=' + leadId;
      const clientSecret = await axios.get(url);
      if (clientSecret.data !== 0) {
        setClientSecret(clientSecret.data);
        setStatus('success');
      } else if (MAX_TRYING_API_CALL > 0) {
        MAX_TRYING_API_CALL = MAX_TRYING_API_CALL - 1;
        await getClientSecret(leadId);
      } else if (MAX_TRYING_API_CALL === 0) {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  useEffect(() => {
    (async () => {
      if (path.indexOf('danke.php') >= 0) {
        const searchParams = new URLSearchParams(window.location.search);
        const secretFromQuery = searchParams.get('secret');

        // If no leadId is stored go to initial screen. Start quiz.
        if ((!leadId || leadId === 'undefined') && !secretFromQuery) {
          const url =
            window.location.origin +
            window.location.pathname.replace('/danke.php', '') +
            window.location.search +
            window.location.hash;
          console.log('CASE 1');
          window.history.pushState({ path: url }, '', url);
        }
        // If no leadId but secret is presented in url query string
        if ((!leadId || leadId === 'undefined') && !!secretFromQuery) {
          dispatch(
            GeneralSlice.actions.setClientSecretUrl(
              getClientSecretTargetUrl(whichUrlShouldBePassed(location.href)).replace(
                '{clientSecretId}',
                secretFromQuery,
              ),
            ),
          );
          dispatch(GeneralSlice.actions.setSecret(secretFromQuery));
          setStatus('success');
        }

        if (leadId && !!secretFromQuery) {
          dispatch(
            GeneralSlice.actions.setClientSecretUrl(
              getClientSecretTargetUrl(whichUrlShouldBePassed(location.href)).replace(
                '{clientSecretId}',
                secretFromQuery,
              ),
            ),
          );
          dispatch(GeneralSlice.actions.setSecret(secretFromQuery));
        }

        if (leadId && !secretFromQuery) {
          loseLead ? setStatus('success') : await getClientSecret(leadId);
        }

        searchParams.set('ls_var', version);
        const newurl =
          window.location.protocol + '//' + window.location.host + window.location.pathname + searchParams.toString()
            ? '?' + searchParams.toString()
            : '';
        window.history.pushState({ path: newurl }, '', newurl);
      }
    })();
  }, [clientSecret, path]);

  useEffect(() => {
    if (clientSecret) {
      const url = getClientSecretTargetUrl(whichUrlShouldBePassed(location.href)).replace(
        '{clientSecretId}',
        clientSecret,
      );
      dispatch(GeneralSlice.actions.setClientSecretUrl(url));
      dispatch(GeneralSlice.actions.setSecret(clientSecret));
    }
  }, [clientSecret]);

  return status;
}
